import React, { useCallback, useEffect, useRef, useState } from "react";
import { growFrameWhenRecaptchaClicked, stopRecaptchaGrow, VERSAPAY_CLIENT_URL } from "../../../utils/versapayFrame";
import {
  getProducts,
  createGiftPurchase,
  createVersapaySession,
  confirmVersapayPayment,
} from "../../../api/api-hook";
import { normalizeInput, usaStates } from "../../../utils/utils";

// ── VersaPay SDK plumbing ─────────────────────────────────────────────────────

const VP_SCRIPT_ID = "versapay-sdk-script-admin";
const VP_SCRIPT_SRC = VERSAPAY_CLIENT_URL;

function startAdminVPTracking() {
  if ((window as any).__adminVpTrackingActive) return;
  (window as any).__adminVpTrackingActive = true;
  (window as any).__adminVpListeners = [];
  const _orig = window.addEventListener.bind(window);
  (window as any).addEventListener = function (type: string, listener: any, options?: any) {
    if (type === "message") ((window as any).__adminVpListeners as any[]).push([listener, options]);
    return _orig(type, listener, options);
  };
}

function removeAdminVPListeners() {
  const listeners: Array<[any, any]> = (window as any).__adminVpListeners || [];
  listeners.forEach(([l, opts]) => window.removeEventListener("message", l as EventListener, opts as any));
  (window as any).__adminVpListeners = [];
}

function killExistingAdminVP() {
  removeAdminVPListeners();
  document.getElementById(VP_SCRIPT_ID)?.remove();
  document.querySelectorAll('iframe[src*="versapay"], iframe[src*="ecommerce-api"]').forEach((el) => el.remove());
  try { (window as any).versapay = undefined; } catch (_) {}
}

async function loadAdminVPScript(): Promise<void> {
  startAdminVPTracking();
  killExistingAdminVP();
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.id = VP_SCRIPT_ID;
    s.src = `${VP_SCRIPT_SRC}?_t=${Date.now()}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load VersaPay SDK."));
    document.body.appendChild(s);
  });
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface GiftProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category: string;
  tabKey: string;
  product_id: string;
  under_25?: string;
}

type Step = 1 | 2 | 3 | 4;

interface PurchaserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY_FORM: PurchaserForm = {
  firstName: "", lastName: "", email: "", phone: "",
  address: "", city: "", state: "VA", zip: "",
};

// ── Brand ─────────────────────────────────────────────────────────────────────

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

// ── Step breadcrumb ───────────────────────────────────────────────────────────

const STEP_LABELS: Record<Step, string> = { 1: "Gift Card", 2: "Purchaser", 3: "Payment", 4: "Done" };

function StepBreadcrumb({ current }: { current: Step }) {
  const steps: Step[] = [1, 2, 3, 4];
  return (
    <div className="d-flex align-items-center gap-1 mb-4 flex-wrap" style={{ fontSize: "0.8rem" }}>
      {steps.map((s) => (
        <React.Fragment key={s}>
          <span style={{
            padding: "0.2rem 0.6rem", borderRadius: 4,
            backgroundColor: s === current ? BRAND_GREEN : s < current ? "#c3e6cb" : "#e9ecef",
            color: s === current ? "#fff" : s < current ? "#155724" : "#6c757d",
            fontWeight: s === current ? 600 : 400,
          }}>
            {STEP_LABELS[s]}
          </span>
          {s < 4 && <span style={{ color: "#adb5bd" }}>›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold" style={{ fontSize: "0.9rem" }}>
      {children}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const PurchaseGiftCard: React.FC = () => {
  const [step, setStep] = useState<Step>(1);
  const [products, setProducts] = useState<GiftProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<GiftProduct | null>(null);
  const [form, setForm] = useState<PurchaserForm>({ ...EMPTY_FORM });
  const formRef = useRef(form);
  formRef.current = form;

  // Payment
  const [paymentMethod, setPaymentMethod] = useState<"credit_card" | "check" | "cash" | "misc">("credit_card");
  const [checkNumber, setCheckNumber] = useState("");
  const [miscNote, setMiscNote] = useState("");

  // VersaPay
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<any>(null);
  const sessionIdRef = useRef("");
  const sessionAmountRef = useRef(0);
  const setupInFlightRef = useRef(false);
  const frameMountedRef = useRef(false);
  const mountedSessionRef = useRef("");
  const callbackBoundSessionRef = useRef("");
  const mountedRef = useRef(true);
  const processingApprovalRef = useRef(false);
  const approvalReceivedRef = useRef(false);
  const handleApprovalRef = useRef<((result: any, sessionId: string) => Promise<void>) | null>(null);
  const clearVPSessionRef = useRef<() => void>(() => {});
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedProductRef = useRef(selectedProduct);
  selectedProductRef.current = selectedProduct;

  const [vpPageLoading, setVpPageLoading] = useState(false);
  const [vpLoading, setVpLoading] = useState(false);
  const [vpError, setVpError] = useState<string | null>(null);
  const [vpRetryNonce, setVpRetryNonce] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  // Result
  const [certificates, setCertificates] = useState<Array<{ promoCode: string; downloadUrl: string }>>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load gift products
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProducts(true);
        const raw = await getProducts({ property: "product_category", value: "vip" });
        const parsed: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) { setProducts([]); return; }
        const sorted = [...parsed].sort((a, b) => (a.price ?? 9999) - (b.price ?? 9999));
        setProducts(sorted);
      } catch {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  const update = (field: keyof PurchaserForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const amountCents = Math.round(Number(selectedProduct?.price ?? 0) * 100);

  // ── VersaPay ─────────────────────────────────────────────────────────────

  const clearVPSession = useCallback(() => {
    if (containerRef.current) containerRef.current.innerHTML = "";
    clientRef.current = null;
    frameMountedRef.current = false;
    callbackBoundSessionRef.current = "";
    mountedSessionRef.current = "";
    sessionIdRef.current = "";
    sessionAmountRef.current = 0;
  }, []);

  const handleApproval = useCallback(async (result: any, sessionToUse: string) => {
    approvalReceivedRef.current = true;
    if (processingApprovalRef.current) return;
    try {
      processingApprovalRef.current = true;
      setProcessingPayment(true);
      setVpLoading(true);
      setVpError(null);

      const f = formRef.current;
      const prod = selectedProductRef.current;

      const confirmResponse = await confirmVersapayPayment({
        sessionId: sessionToUse,
        approvalResult: result,
        amount: amountCents,
        firstName: f.firstName,
        lastName: f.lastName,
        email: f.email,
        address1: f.address,
        city: f.city,
        state: f.state,
        zip: f.zip,
        lineItems: [{ type: "Item", number: prod?.product_id || "GIFT", description: prod?.label || prod?.name || "Gift Card", price: amountCents, quantity: 1, discount: 0 }],
      } as any);

      if (!confirmResponse?.success) throw new Error(confirmResponse?.error || "Failed to confirm payment.");

      const payload: any = {
        firstName: f.firstName, lastName: f.lastName, email: f.email, phone: f.phone,
        ShippingAddress: f.address, ShippingCity: f.city, ShippingState: f.state, ShippingZip: f.zip,
        cart_total: prod?.price ?? 0,
        GiftPurchase: true,
        GiftVipPackage: prod?.tabKey || "",
        GiftProductId: prod?.id || "",
        product_id: prod?.product_id || "",
        payment_method: "Credit Card",
        versapayTransactionId: confirmResponse.transactionId,
        versapayOrderNumber: confirmResponse.orderNumber ?? "",
        versapayVpOrderNumber: confirmResponse.vpOrderNumber ?? "",
        ccToken: confirmResponse.ccToken ?? "",
        ccLastFour: confirmResponse.ccLastFour ?? "",
        ccType: confirmResponse.ccType ?? "",
        created_source: "Admin",
};

      const resp = await createGiftPurchase(payload);
      const data = typeof resp === "string" ? JSON.parse(resp) : resp;
      setCertificates(data?.certificates || []);
      setStep(4);
    } catch (err: any) {
      const status = err?.response?.status;
      const bodyText: string = err?.response?.data ?? err?.message ?? "";
      const is504 = status === 504 || (typeof bodyText === "string" && bodyText.includes("504"));
      setPayError(is504
        ? "The request timed out. Your card has not been charged — please wait and try again."
        : err?.response?.data?.error || err?.response?.data?.message || err?.message || "Payment approved but processing failed.");
    } finally {
      processingApprovalRef.current = false;
      setProcessingPayment(false);
      setVpLoading(false);
    }
  }, [amountCents]);

  handleApprovalRef.current = handleApproval;
  clearVPSessionRef.current = clearVPSession;

  // VersaPay setup — runs when on step 3 with credit card
  useEffect(() => {
    if (step !== 3) return;

    const setup = async () => {
      if (paymentMethod !== "credit_card") { clearVPSession(); setVpPageLoading(false); return; }
      if (!containerRef.current || !selectedProduct) { setVpPageLoading(true); return; }
      if (!amountCents || amountCents <= 0) { setVpPageLoading(false); return; }

      if (frameMountedRef.current && sessionIdRef.current && mountedSessionRef.current === sessionIdRef.current && sessionAmountRef.current === amountCents) {
        setVpPageLoading(false); return;
      }

      if (setupInFlightRef.current) return;
      setupInFlightRef.current = true;
      setVpPageLoading(true);
      setVpError(null);

      try {
        await loadAdminVPScript();
        sessionIdRef.current = ""; sessionAmountRef.current = 0;
        if (!mountedRef.current || !containerRef.current) return;
        if (!window.versapay) throw new Error("VersaPay SDK did not load.");

        const sessResp = await createVersapaySession({
          amount: amountCents,
          firstName: formRef.current.firstName,
          lastName: formRef.current.lastName,
          email: formRef.current.email,
        });
        if (!sessResp?.sessionId) throw new Error("No VersaPay session ID returned.");
        const sessionToUse = sessResp.sessionId;
        sessionIdRef.current = sessionToUse;
        sessionAmountRef.current = amountCents;

        if (!mountedRef.current || !containerRef.current) return;

        const client = await window.versapay.initClient(sessionToUse);
        clientRef.current = client;

        if (!mountedRef.current || !containerRef.current) return;

        if (client.onApproval) {
          const boundSession = sessionToUse;
          client.onApproval(
            (result: any) => { stopRecaptchaGrow(); handleApprovalRef.current?.(result, boundSession); },
            (err: any) => {
              stopRecaptchaGrow();
              if (safetyTimeoutRef.current) { clearTimeout(safetyTimeoutRef.current); safetyTimeoutRef.current = null; }
              const msg = typeof err === "string" ? err : err?.message || err?.error || "Payment not approved.";
              setVpError(msg);
              setProcessingPayment(false);
              setVpLoading(false);
              clearVPSessionRef.current?.();
            }
          );
          callbackBoundSessionRef.current = sessionToUse;
        }

        if (!frameMountedRef.current || mountedSessionRef.current !== sessionToUse) {
          containerRef.current.innerHTML = "";
          await client.initFrame(containerRef.current, "600px", "720px");
          if (!mountedRef.current || !containerRef.current) return;
          const vpIframe = containerRef.current.querySelector("iframe");
          if (vpIframe) { vpIframe.style.width = "100%"; vpIframe.style.maxWidth = "100%"; vpIframe.style.height = "270px"; }
          frameMountedRef.current = true;
          mountedSessionRef.current = sessionToUse;
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setVpError(err?.response?.data?.error || err?.message || "Failed to load payment form.");
          frameMountedRef.current = false;
          mountedSessionRef.current = "";
        }
      } finally {
        setupInFlightRef.current = false;
        if (mountedRef.current) setVpPageLoading(false);
      }
    };

    setup();
  }, [step, selectedProduct, amountCents, vpRetryNonce, paymentMethod]);

  const handlePay = async () => {
    try {
      setVpError(null);
      if (vpLoading || processingPayment || vpPageLoading) return;
      if (!clientRef.current) throw new Error("Payment form not ready.");
      approvalReceivedRef.current = false;
      setProcessingPayment(true);
      (window as any).__adminVpSessionWasUsed = true;
      growFrameWhenRecaptchaClicked(containerRef.current?.querySelector("iframe"));
      await clientRef.current.submitEvents();
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = setTimeout(() => {
        safetyTimeoutRef.current = null;
        if (!approvalReceivedRef.current && !processingApprovalRef.current) {
          setVpError("Payment session expired. Please re-enter your card details and try again.");
          setProcessingPayment(false);
          clearVPSessionRef.current?.();
        }
      }, 60000);
    } catch (err: any) {
      stopRecaptchaGrow();
      const vpIframe = containerRef.current?.querySelector("iframe");
      if (vpIframe) vpIframe.style.height = "270px";
      setVpError(err?.response?.data?.message || err?.message || "Could not submit payment.");
      setProcessingPayment(false);
    }
  };

  const handleCancelPayment = () => {
    setProcessingPayment(false); setVpLoading(false);
    setVpError(null); setPayError(null);
    processingApprovalRef.current = false;
    approvalReceivedRef.current = false;
    clearVPSession();
    setVpRetryNonce((n) => n + 1);
  };

  const handleOfflinePayment = async () => {
    if (processingPayment) return;
    if (paymentMethod === "check" && !checkNumber.trim()) return;
    setProcessingPayment(true); setVpLoading(true); setVpError(null); setPayError(null);
    try {
      const f = formRef.current;
      const prod = selectedProductRef.current;
      const pmLabel = { credit_card: "Credit Card", check: "Check", cash: "Cash", misc: "Miscellaneous" }[paymentMethod];
      const payload: any = {
        firstName: f.firstName, lastName: f.lastName, email: f.email, phone: f.phone,
        ShippingAddress: f.address, ShippingCity: f.city, ShippingState: f.state, ShippingZip: f.zip,
        cart_total: prod?.price ?? 0,
        GiftPurchase: true,
        GiftVipPackage: prod?.tabKey || "",
        GiftProductId: prod?.id || "",
        product_id: prod?.product_id || "",
        payment_method: pmLabel,
        ...(paymentMethod === "check" ? { check_number: checkNumber.trim() } : {}),
        ...(paymentMethod === "misc" && miscNote.trim() ? { misc_note: miscNote.trim() } : {}),
      };
      const resp = await createGiftPurchase(payload);
      const data = typeof resp === "string" ? JSON.parse(resp) : resp;
      setCertificates(data?.certificates || []);
      setStep(4);
    } catch (err: any) {
      setPayError(err?.response?.data?.error || err?.message || "Order processing failed.");
    } finally {
      setProcessingPayment(false); setVpLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleStartOver = () => {
    clearVPSession();
    (window as any).__adminVpSessionWasUsed = false;
    setSelectedProduct(null);
    setForm({ ...EMPTY_FORM });
    setStep(1);
    setCertificates([]);
    setPayError(null); setVpError(null);
    setPaymentMethod("credit_card");
    setCheckNumber(""); setMiscNote("");
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <h4 className="mb-1" style={{ color: BRAND_GREEN }}>Purchase Gift Card</h4>
      <p className="text-muted small mb-4">
        Process a VIP Gift Card purchase on behalf of a customer. A promo code and printable gift certificate will be generated.
      </p>

      <StepBreadcrumb current={step} />

      {/* ── STEP 1: Select Gift Card Amount ── */}
      {step === 1 && (
        <div>
          <SectionHeader>Choose Gift Card Amount</SectionHeader>
          {loadingProducts ? (
            <div className="text-muted">Loading gift card options…</div>
          ) : products.length === 0 ? (
            <div className="text-muted">No gift card products available.</div>
          ) : (
            <div className="d-flex flex-wrap gap-2 mb-4">
              {products.map((p) => {
                const isActive = selectedProduct?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`btn btn-sm ${isActive ? "btn-success" : "btn-outline-secondary"}`}
                    style={{
                      ...(isActive ? { backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN } : {}),
                      flex: "1 1 0",
                      minWidth: 90,
                      maxWidth: 160,
                      padding: "0.4rem 0.6rem",
                    }}
                    onClick={() => setSelectedProduct(p)}
                  >
                    <div className="fw-bold" style={{ fontSize: "0.95rem" }}>
                      ${p.price?.toFixed(2)}
                    </div>
                    <div style={{ fontSize: "0.72rem", opacity: 0.85 }}>{p.label || p.name}</div>
                  </button>
                );
              })}
            </div>
          )}

          {selectedProduct && (
            <div className="alert border-0 mb-4" style={{ backgroundColor: "rgba(2,56,34,0.06)" }}>
              <div className="fw-semibold mb-1">{selectedProduct.label || selectedProduct.name}</div>
              {selectedProduct.description && (
                <div className="small text-muted" style={{ whiteSpace: "pre-wrap" }}>{selectedProduct.description}</div>
              )}
              <div className="mt-2 fw-bold" style={{ color: BRAND_GREEN }}>
                ${selectedProduct.price?.toFixed(2)}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              className="btn btn-primary px-4"
              disabled={!selectedProduct}
              onClick={() => setStep(2)}
              style={{ backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN }}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Purchaser Info ── */}
      {step === 2 && (
        <div>
          <SectionHeader>Purchaser Information</SectionHeader>
          <div className="mb-3 p-3 border rounded bg-light">
            <span className="fw-semibold">Gift Card: </span>
            {selectedProduct?.label || selectedProduct?.name} — <strong>${selectedProduct?.price?.toFixed(2)}</strong>
          </div>

          <form
            className="row gy-3"
            onSubmit={(e) => { e.preventDefault(); setStep(3); }}
          >
            <div className="col-md-6">
              <label className="form-label">First Name</label>
              <input type="text" className="form-control" value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                placeholder="First name" required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Last Name</label>
              <input type="text" className="form-control" value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                placeholder="Last name" required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Email <span className="text-danger">*</span></label>
              <input type="email" className="form-control" value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="email@example.com" required />
              <div className="form-text">Required — the gift certificate will be sent here.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label">Phone <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>(optional)</span></label>
              <input type="tel" className="form-control" value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                onKeyUp={(e) => update("phone", normalizeInput(e.currentTarget.value))}
                placeholder="(555) 555-5555" maxLength={14} />
            </div>
            <div className="col-12">
              <label className="form-label">Address <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>(optional)</span></label>
              <input type="text" className="form-control" value={form.address}
                onChange={(e) => update("address", e.target.value)}
                placeholder="123 Main St" />
            </div>
            <div className="col-md-5">
              <label className="form-label">City</label>
              <input type="text" className="form-control" value={form.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="City" />
            </div>
            <div className="col-md-4">
              <label className="form-label">State</label>
              <select className="form-select" value={form.state} onChange={(e) => update("state", e.target.value)}>
                <option value="">Select…</option>
                {usaStates.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">ZIP</label>
              <input type="text" className="form-control" value={form.zip}
                onChange={(e) => update("zip", e.target.value)}
                placeholder="ZIP" maxLength={5} inputMode="numeric" pattern="[0-9]*" />
            </div>

            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4"
                style={{ backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN }}>
                Next →
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 3: Payment ── */}
      {step === 3 && (
        <div>
          <SectionHeader>Payment</SectionHeader>

          <div className="mb-4 p-3 border rounded bg-light">
            <div className="fw-semibold">{form.firstName} {form.lastName}</div>
            {form.email && <div className="small text-muted">{form.email}</div>}
            <div className="mt-2">
              <span className="fw-semibold">Gift Card: </span>
              {selectedProduct?.label || selectedProduct?.name} — <strong>${selectedProduct?.price?.toFixed(2)}</strong>
            </div>
          </div>

          {/* Payment method selector */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Payment Method</label>
            <div className="d-flex gap-2 flex-wrap">
              {(["credit_card", "check", "cash", "misc"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn btn-sm ${paymentMethod === m ? "btn-dark" : "btn-outline-secondary"}`}
                  onClick={() => { setPaymentMethod(m); setVpError(null); setPayError(null); clearVPSession(); setVpRetryNonce((n) => n + 1); }}
                >
                  {{ credit_card: "Credit Card", check: "Check", cash: "Cash", misc: "Misc" }[m]}
                </button>
              ))}
            </div>
          </div>

          {paymentMethod === "credit_card" && (
            <div>
              {vpPageLoading && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="spinner-border spinner-border-sm" role="status" aria-label="Loading" />
                  <span className="small text-muted">Loading payment form…</span>
                </div>
              )}
              <div ref={containerRef} style={{ minHeight: 40 }} />
              {vpError && (
                <div className="alert alert-danger mt-3 py-2 small">
                  {vpError}
                  <button className="btn btn-sm btn-outline-danger ms-3" onClick={() => { clearVPSession(); setVpRetryNonce((n) => n + 1); }}>
                    Retry
                  </button>
                </div>
              )}
              {processingPayment ? (
                <div className="d-flex flex-column align-items-center justify-content-center gap-2 mt-4 py-3">
                  <div className="spinner-border" role="status" style={{ color: BRAND_GREEN, width: "2rem", height: "2rem" }} />
                  <span className="small text-muted">Processing payment…</span>
                  <button className="btn btn-sm btn-outline-secondary mt-1" onClick={handleCancelPayment}>Cancel</button>
                </div>
              ) : (
                <div className="d-flex justify-content-between mt-3">
                  <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(2)}>← Back</button>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handlePay}
                    disabled={vpLoading || vpPageLoading || processingPayment}
                    style={{ backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN }}
                  >
                    {vpLoading ? "Processing…" : `Pay $${selectedProduct?.price?.toFixed(2)}`}
                  </button>
                </div>
              )}
            </div>
          )}

          {paymentMethod !== "credit_card" && (
            <div>
              {paymentMethod === "check" && (
                <div className="mb-3" style={{ maxWidth: 320 }}>
                  <label className="form-label">Check Number <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="Check number" required />
                </div>
              )}
              {paymentMethod === "misc" && (
                <div className="mb-3" style={{ maxWidth: 420 }}>
                  <label className="form-label">Note <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>(optional)</span></label>
                  <input type="text" className="form-control" value={miscNote}
                    onChange={(e) => setMiscNote(e.target.value)}
                    placeholder="Payment note" />
                </div>
              )}
              {payError && <div className="alert alert-danger py-2 small">{payError}</div>}
              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(2)}>← Back</button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleOfflinePayment}
                  disabled={processingPayment || (paymentMethod === "check" && !checkNumber.trim())}
                  style={{ backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN }}
                >
                  {processingPayment ? "Processing…" : "Complete Order"}
                </button>
              </div>
            </div>
          )}

          {payError && paymentMethod === "credit_card" && (
            <div className="alert alert-danger mt-3 py-2 small">{payError}</div>
          )}
        </div>
      )}

      {/* ── STEP 4: Done ── */}
      {step === 4 && (
        <div>
          <SectionHeader>Order Complete</SectionHeader>
          <div className="alert alert-success mb-4">
            <strong>Gift card order processed successfully!</strong>
            <div className="mt-1 small">
              A gift certificate has been generated for <strong>{form.firstName} {form.lastName}</strong>
              {form.email ? ` (${form.email})` : ""}.
            </div>
          </div>

          {certificates.length > 0 && (
            <div className="mb-4">
              <h6 className="fw-semibold mb-3">Gift Certificate{certificates.length > 1 ? "s" : ""}</h6>
              {certificates.map((cert, i) => (
                <div key={i} className="border rounded p-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <div className="small text-muted">Promo Code</div>
                    <div className="fw-bold" style={{ fontSize: "1.05rem", letterSpacing: "0.05em" }}>{cert.promoCode}</div>
                  </div>
                  {cert.downloadUrl && (
                    <a
                      href={cert.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-secondary"
                    >
                      <i className="bi bi-download me-1" />
                      Download Certificate
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="p-3 border rounded bg-light mb-4">
            <div className="row g-2 small">
              <div className="col-md-6"><span className="text-muted">Purchaser:</span> {form.firstName} {form.lastName}</div>
              {form.email && <div className="col-md-6"><span className="text-muted">Email:</span> {form.email}</div>}
              <div className="col-md-6"><span className="text-muted">Gift Card:</span> {selectedProduct?.label || selectedProduct?.name}</div>
              <div className="col-md-6"><span className="text-muted">Amount:</span> ${selectedProduct?.price?.toFixed(2)}</div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary px-4"
            onClick={handleStartOver}
            style={{ backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN }}
          >
            Process Another Gift Card
          </button>
        </div>
      )}
    </div>
  );
};

export default PurchaseGiftCard;
